import { Component, ContentChildren, EventEmitter, Input, Output, QueryList } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgModel } from '@angular/forms';

/** Optional guard run before the section opens (e.g. a warning modal); false keeps it closed. */
export type BeforeOpenGuard = () => boolean | Promise<boolean>;

let nextSectionId = 0;

/**
 * Section with a clickable header that shows or hides its content (C2027-48).
 *
 * The content is hidden, not removed: form controls inside a closed section stay
 * registered in the parent ngForm, so the form keeps validating them. The header
 * flags the section when one of those controls is invalid, otherwise a required
 * field in a closed section would disable "Save" with no visible reason.
 *
 * Usage:
 *   <app-collapsible-section [title]="'distributeur.edit.block.general' | t" [open]="true">
 *     ...fields...
 *   </app-collapsible-section>
 */
@Component({
  standalone: true,
  selector: 'app-collapsible-section',
  templateUrl: 'collapsible-section.component.html',
  imports: [CommonModule],
  styles: [
    `
      .collapsible-section {
        border: 1px solid #ddd;
        border-radius: 4px;
        margin-bottom: 15px;
      }
      .collapsible-section__header {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        padding: 10px 15px;
        border: 0;
        border-radius: 4px;
        background: #f5f5f5;
        font-size: 16px;
        font-weight: bold;
        text-align: left;
      }
      .collapsible-section--open .collapsible-section__header {
        border-bottom: 1px solid #ddd;
        border-radius: 4px 4px 0 0;
      }
      .collapsible-section__title {
        flex: 1;
      }
      .collapsible-section__body {
        padding: 15px;
      }
    `,
  ],
})
export class CollapsibleSectionComponent {
  /** Header text, already translated by the caller. */
  @Input() title = '';
  @Input() open = false;
  @Input() beforeOpen: BeforeOpenGuard;
  @Output() openChange = new EventEmitter<boolean>();

  @ContentChildren(NgModel, { descendants: true }) controls: QueryList<NgModel>;

  readonly contentId = `collapsible-section-${nextSectionId++}`;
  private toggling = false;

  get hasInvalidControls(): boolean {
    return !!this.controls?.some((control) => control.invalid);
  }

  async toggle(): Promise<void> {
    if (this.toggling) {
      return;
    }
    this.toggling = true;
    try {
      if (!this.open && this.beforeOpen && !(await this.beforeOpen())) {
        return;
      }
      this.open = !this.open;
      this.openChange.emit(this.open);
    } finally {
      this.toggling = false;
    }
  }
}
